package com.xtream.ui.ui.preference

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusGroup
import androidx.compose.foundation.focusable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.asPaddingValues
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBars
import androidx.compose.foundation.layout.systemBars
import androidx.compose.foundation.layout.wrapContentSize
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.ModalBottomSheetProperties
import androidx.compose.material3.RadioButton
import androidx.compose.material3.RadioButtonDefaults
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.scale
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusProperties
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.focus.focusRestorer
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.graphics.vector.rememberVectorPainter
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import com.xtream.ui.data.PreferenceAction
import com.xtream.ui.data.PreferenceButtonAction
import com.xtream.ui.data.PreferenceCategoryAction
import com.xtream.ui.data.PreferenceChoiceAction
import com.xtream.ui.data.PreferenceRadioAction
import com.xtream.ui.data.PreferenceSelectableAction
import com.xtream.ui.data.PreferenceSwitchAction
import com.xtream.ui.data.PreferenceType
import com.xtream.ui.ui.component.extension.ifElse
import com.xtream.ui.ui.util.extensions.lightenColor
import com.xtream.ui.ui.widget.TvButton
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource


// ─────────────────────────────────────────────────────────────────────────────
// GetCompatiblePrefButton
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun GetCompatiblePrefButton(
    action: PreferenceAction,
    modifier: Modifier = Modifier,
    onClick: ((PreferenceAction) -> Unit)? = null,
    dialogsModifier: Modifier,
) {
    // ✅ sealed class — compiler-enforced exhaustive when, no ordering bugs
    when (action) {
        is PreferenceCategoryAction -> PrefsBaseContent(
            modifier = modifier
                .focusable(false)
                .padding(top = 12.dp),
            title = stringResource(action.title),
            description = action.description?.let {
                stringResource(it)
            },
        )

        is PreferenceButtonAction ->     TvButton(
        fillParent = true,
        
            modifier = modifier.heightIn(min = 70.dp),
            title = stringResource(action.title),
            subtitle = action.description?.let {
                stringResource(it)
            }?:"",
            onClick = action.onClick,
            // ✅ was inverted before — always passed null and would crash on !!
            startContent = action.icon?.let {
                {
                    Icon(
                        imageVector = it,
                        contentDescription = stringResource(action.title),
                        modifier = Modifier.scale(.7f),
                    )
                }
             },
        )

        // ✅ PreferenceChoiceAction is now a sealed sibling, not a subclass —
        //    safe to check before or after PreferenceRadioAction
        is PreferenceChoiceAction -> InTvComposePrefsDialogButton(
            dialogAction = action,
            modifier = modifier,
            dialogsModifier = dialogsModifier,
            onClick = onClick,
        )

        is PreferenceRadioAction -> InTvComposePrefsDialogButton(
            dialogAction = action,
            modifier = modifier,
            dialogsModifier = dialogsModifier,
            onClick = onClick,
        )

        is PreferenceSwitchAction -> InTvComposePrefsSwitchButton(
            modifier = modifier,
            switchAction = action,
        )
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// InTvComposePrefsDialogButton  (Radio + Choice)
// ─────────────────────────────────────────────────────────────────────────────
@Composable
fun InTvComposePrefsDialogButton(
    dialogAction: PreferenceSelectableAction,  // ✅ was PreferenceAction — had no options/currentValue
    modifier: Modifier = Modifier,
    dialogsModifier: Modifier,
    onClick: ((PreferenceAction) -> Unit)? = null,
) {
    var isShowDialog by remember { mutableStateOf(false) }

    val subtitle = dialogAction.currentValue
        ?.let { dialogAction.options.getOrElse(it) { null }?.toString() }


    if (isShowDialog) {
        DropDownPrefDialog(
            modifier = dialogsModifier,
            selectedKey = dialogAction.currentValue,
            preferenceAction = dialogAction,
            onDismissRequest = { isShowDialog = false },
            onConfirmation = { k, v ->
                dialogAction.onItemSelected?.invoke(k, v)
                isShowDialog = false
            },
            dialogTitle = stringResource(dialogAction.title),
            //  options = dialogAction.options,
        )
    }

        TvButton(
        fillParent = true,
        
         title =
             stringResource(dialogAction.title)?:""
         ,
        modifier = modifier,
        subtitle = subtitle?: dialogAction.description?.let {
            stringResource(it)
        },
        
        onClick = {
            if (onClick != null) onClick(dialogAction)
            else isShowDialog = true
        },
        leadingIcon =  dialogAction.icon?.let { rememberVectorPainter(it) },


    )
}

// ─────────────────────────────────────────────────────────────────────────────
// InTvComposePrefsSwitchButton
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun InTvComposePrefsSwitchButton(
    modifier: Modifier = Modifier,
    switchAction: PreferenceSwitchAction,
) {
    // ✅ currentValue replaces isToggledFlow?.collectAsState()
        TvButton(
        fillParent = true,
        
        title = stringResource(switchAction.title)?:"",
        modifier = modifier,
        subtitle = stringResource(switchAction.description)?:"",
         onClick = {
            switchAction.onCheckedChange?.invoke(!switchAction.currentValue)
        },
        leadingIcon =  switchAction.icon?.let { rememberVectorPainter(it) },

        trailingContent = {
            Switch(
                modifier = Modifier
                    .scale(.7f)
                    .focusProperties { canFocus = false }
                    .clickable(false) {},
                colors = SwitchDefaults.colors(
                    checkedThumbColor = MaterialTheme.colorScheme.primary,
                    checkedTrackColor = MaterialTheme.colorScheme.primary.copy(alpha = .3f)

                ),
                checked = switchAction.currentValue, // ✅ plain value, no Flow
                onCheckedChange = switchAction.onCheckedChange,
            )
        },
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// PrefsBaseContent
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun PrefsBaseContent(
    title: String,
    modifier: Modifier = Modifier,
    description: String? = null,
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.Start,
    ) {
        Text(
            text = title,
            fontWeight = FontWeight.Bold,
            maxLines = 1,
            style = MaterialTheme.typography.bodyMedium,
        )
        if (!description.isNullOrEmpty()) {
            Text(
                modifier = Modifier.padding(top = 8.dp),
                text = description,
                fontStyle = FontStyle.Italic,
                maxLines = 2,
                style = MaterialTheme.typography.bodySmall,
            )
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// DropDownPrefDialog
// ─────────────────────────────────────────────────────────────────────────────
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DropDownPrefDialog(
    onDismissRequest: () -> Unit,
    onConfirmation: (key: String, value: Any) -> Unit,
    dialogTitle: String,
    modifier: Modifier = Modifier,
    preferenceAction: PreferenceSelectableAction,
    // ✅ removed options param — use preferenceAction.options directly
    selectedKey: String? = null,
) {
    val focusRequester = remember { FocusRequester() }
    val selectionType = preferenceAction.prefType
    val sheetState = rememberModalBottomSheetState( skipPartiallyExpanded = false)
    val statusBarPadding = WindowInsets.statusBars.asPaddingValues().calculateTopPadding()

    LaunchedEffect(Unit) { sheetState.show() }

    val entries = remember(preferenceAction.options) { preferenceAction.options.entries.toList() }

    ModalBottomSheet(
        sheetState = sheetState,
        properties = ModalBottomSheetProperties(),
        containerColor = MaterialTheme.colorScheme.background.lightenColor(.1f),
        dragHandle = null,

        modifier = modifier
            .padding(top = statusBarPadding, start = 4.dp, end = 4.dp)
            .focusProperties { canFocus = false }
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onDismissRequest,
            ),
        onDismissRequest = onDismissRequest,
    ) {
        LazyColumn(
            modifier = Modifier
                .focusRestorer ( focusRequester )
                .focusGroup(),
            contentPadding = PaddingValues(24.dp, 24.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            stickyHeader {
                Text(
                    text = dialogTitle,
                    fontWeight = FontWeight.Bold,
                    style = MaterialTheme.typography.titleLarge,
                )
                Spacer(Modifier.heightIn(min = 6.dp))

                Text(
                    text = preferenceAction.description?.let {
                        stringResource(it)
                    }?:"",
                    fontWeight = FontWeight.Normal,
                    style = MaterialTheme.typography.bodyMedium,
                )

            }

            itemsIndexed(
                entries,
                key = { _, entry -> entry.key },
            ) { index, entry ->
                val itemModifier = Modifier
                    .ifElse(
                        index == 0,
                        Modifier.focusRequester(focusRequester),
                    )
                    .fillMaxWidth()
                    .graphicsLayer{
                        alpha = if (entry.key == selectedKey) 1f else .8f
                    }

                val title: String = when(entry.value){
                    is String -> entry.value.toString()
                    is StringResource -> stringResource(entry.value as StringResource)
                    else -> entry.value.toString()
                }
                when (selectionType) {
                    PreferenceType.RADIO -> InTvRadioButton(
                        selected = entry.key == selectedKey,
                        modifier = itemModifier,
                        key = entry.key,
                        value = title,
                        onConfirmation = onConfirmation,
                    )
                    PreferenceType.CHECKBOX -> InTvCheckBoxButtons(
                        checked = entry.key == selectedKey,
                        modifier = itemModifier,
                        key = entry.key,
                        value =title,
                        onConfirmation = onConfirmation,
                    )
                    else ->     TvButton(
        fillParent = true,
        
                        title = title,
                        modifier = itemModifier,
                        onClick = { onConfirmation(entry.key, title) },
                    )
                }
            }
        }
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// InTvChoiceButtons
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun InTvChoiceButtons(
    modifier: Modifier = Modifier,
    title: String,
    onConfirmation: (key: String, value: Any) -> Unit,
    onClick: ((PreferenceAction) -> Unit)? = null,
    choiceAction: PreferenceChoiceAction,
) {
        TvButton(
        fillParent = true,
        
        title = title,
        modifier = modifier,
        onClick = {
            if (onClick != null) onClick(choiceAction)
            // ✅ was passing entire options Map as value — now passes the title
            else onConfirmation(choiceAction.key, title)
        },
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// InTvRadioButton
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun InTvRadioButton(
    modifier: Modifier = Modifier,
    value: String,
    key: String,
    selected: Boolean = false,
    onConfirmation: (key: String, value: Any) -> Unit,
) {
        TvButton(
        fillParent = true,
        
        title = value,
        modifier = modifier,
        startContent = {
            RadioButton(
                colors = RadioButtonDefaults.colors(
                    selectedColor = MaterialTheme.colorScheme.primary,
                ),
                selected = selected,
                onClick = { onConfirmation(key, value) },
            )
        },
         onClick = { onConfirmation(key, value) },
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// InTvCheckBoxButtons
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun InTvCheckBoxButtons(
    modifier: Modifier = Modifier,
    value: String,
    key: String,
    checked: Boolean = false,
    onConfirmation: (key: String, value: Any) -> Unit,
) {
        TvButton(
        fillParent = true,
        
        title = value,
        modifier = modifier,
        startContent = {
            Checkbox(
                checked = checked,
                onCheckedChange = null, // ✅ clicks handled by the row onClick
            )
        },
         onClick = { onConfirmation(key, value) },
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// ProgressDialog
// ─────────────────────────────────────────────────────────────────────────────

@Composable
fun ProgressDialog(
    showProgressDialog: Boolean,
    onDismissRequest: () -> Unit,
) {
    BaseResultDialog(
        onDismissRequest = onDismissRequest,
        showProgressDialog = showProgressDialog,
        isDismissible = false,
        modifier = Modifier
            .size(100.dp)
            .clip(RoundedCornerShape(8.dp)),
    ) {
        CircularProgressIndicator(modifier = Modifier.padding(16.dp))
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// BaseResultDialog
// ─────────────────────────────────────────────────────────────────────────────

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun BaseResultDialog(
    modifier: Modifier = Modifier,
    showProgressDialog: Boolean,
    isDismissible: Boolean = false,
    onDismissRequest: () -> Unit,
    content: @Composable () -> Unit,
) {
    if (!showProgressDialog) return // ✅ early return — flattens nesting

    BasicAlertDialog(
        modifier = modifier,
        onDismissRequest = onDismissRequest,
        properties = DialogProperties(
            dismissOnBackPress = isDismissible,
            dismissOnClickOutside = isDismissible,
        ),
        content = content, // ✅ pass directly, no need for { content.invoke() }
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// PreferencesComposeDialog  (unchanged logic, cleaned formatting)
// ─────────────────────────────────────────────────────────────────────────────

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PreferencesComposeDialog(
    title: String,
    preferences: List<PreferenceAction>,
    widthPercent: Float = .35f,
    showProgressDialog: Boolean = true,
    onDismissRequest: () -> Unit,
) {
    BaseResultDialog(
        showProgressDialog = showProgressDialog,
        isDismissible = true,
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.7f))
            .focusProperties { canFocus = false }
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onDismissRequest,
            ),
        onDismissRequest = onDismissRequest,
    ) {
        SettingsComposeScreen(
            modifier = Modifier
                .padding(16.dp)
                .padding(bottom = WindowInsets.systemBars.asPaddingValues().calculateBottomPadding())
                .wrapContentSize(Alignment.CenterEnd)
                .shadow(15.dp, shape = RoundedCornerShape(15.dp))
                .border(1.dp, MaterialTheme.colorScheme.onBackground.copy(alpha = .05f), shape = RoundedCornerShape(15.dp))
                .clip(RoundedCornerShape(15.dp))
                .focusProperties { canFocus = false }
                .clickable(false) {}
                .fillMaxWidth(widthPercent),
            title = title,
            preferences = preferences,
        )
    }
}